import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NorthWestIcon from '@mui/icons-material/NorthWest';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Tournament } from '@/types';
import styles from './styles.module.scss';

export interface SuggestionRowProps {
  tournament: Tournament;
  /**
   * A plain tap: opens the tournament in place of the link (search mode leaves
   * its history entry first). Modifier / middle clicks keep the link behaviour.
   */
  onSelect: (tournament: Tournament) => void;
  /** ↖ — copies the tournament name into the field; without it the button is hidden. */
  onFill?: (value: string) => void;
}

/** Keeps the search field focused while a row is pressed. */
const keepFieldFocus = (e: React.MouseEvent) => e.preventDefault();

/** A plain click opens the tournament here; modifier / middle clicks open a new tab instead. */
const isPlainClick = (e: React.MouseEvent) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

/**
 * One tournament in the header search results (`<li>`): logo, name,
 * `league · season` and the status badge — the whole row opens the tournament —
 * and the optional ↖ fill button next to it (a sibling, never nested).
 */
export const SuggestionRow: React.FC<SuggestionRowProps> = ({ tournament, onSelect, onFill }) => {
  const { t } = useTranslation('common');

  return (
    <li className={onFill ? styles.row : `${styles.row} ${styles.rowNoFill}`}>
      <Link
        to={`/tournaments/${tournament.id}`}
        className={styles.main}
        onMouseDown={keepFieldFocus}
        onClick={(e) => {
          if (!isPlainClick(e)) return;
          e.preventDefault();
          onSelect(tournament);
        }}
        data-testid={`header-search-suggestion-${tournament.id}`}
      >
        {tournament.leagueLogo ? (
          <img
            className={styles.logo}
            src={tournament.leagueLogo}
            alt=""
            width={32}
            height={32}
            decoding="async"
          />
        ) : (
          <span className={styles.logoPlaceholder} aria-hidden="true" />
        )}
        <span className={styles.text}>
          <span className={styles.title}>{tournament.name}</span>
          {(tournament.leagueName || tournament.season) && (
            // Season stays whole (it tells same-name tournaments apart); the league truncates.
            <span className={styles.subtitle}>
              {tournament.leagueName && <span className={styles.league}>{tournament.leagueName}</span>}
              {tournament.season && <span className={styles.season}>{tournament.season}</span>}
            </span>
          )}
        </span>
        {tournament.listStatus && <StatusBadge status={tournament.listStatus} variant="soft" />}
      </Link>
      {onFill && (
        <button
          type="button"
          className={styles.fill}
          onMouseDown={keepFieldFocus}
          onClick={() => onFill(tournament.name)}
          aria-label={t('headerSearch.fill')}
          data-testid={`header-search-suggestion-fill-${tournament.id}`}
        >
          <NorthWestIcon />
        </button>
      )}
    </li>
  );
};

/** List of `SuggestionRow`s (dividers between rows). */
export const SuggestionList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className={styles.list}>{children}</ul>
);
